import {DestroyRef, Injectable, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {
  BUTTON_COLOR,
  BUTTON_SIZE,
  BUTTON_TYPE,
} from '@discocil/fv-components';
import {TranslateService} from '@ngx-translate/core';
import {
  Observable,
  Subject,
  catchError,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {AccountModeService} from 'src/app/core/entities/account/account-modes/account-mode.service';
import {ACCOUNT_MODE} from 'src/app/core/entities/account/account-modes/enum/account-mode.enum';
import {AccountSocketHandlerService} from 'src/app/core/entities/account/account-socket-handler.service';
import {DeviceService} from 'src/app/core/entities/device/device.service';
import {APP_ROUTE_PATH} from 'src/app/core/enums';
import {Logger} from 'src/app/core/interfaces/Logger';
import {IWaiter} from 'src/app/core/interfaces/waiter.interface';
import {SocketConnectionHandlerService} from 'src/app/core/services/socket/socket-connection-handler.service';
import {GlobalConfirmService} from 'src/app/shared/services/global-confirm/global-confirm.service';
import {IAuthWaiter} from '../../interfaces/waiter-auth.interface';
import {EVENT_BUS} from 'src/app/shared/domain/tokens/event-bus.token';
import {INFORMATIVE_ROUTE_PATH} from '@core/enums/routes/informative-route-path.enum';
import {SERVICE_STATE_SERVICE} from '@features/service/domain/tokens/service-state.token';
import {Worker} from '@features/service/domain/models/worker.model';
import {WaiterSessionEndedEvent} from "@platform/event-bus/domain/events/waiter/waiter-session-ended.event";

/**
 * Service responsible for waiter authentication operations
 *
 * Handles authentication flows including:
 * - Sign in and sign out of waiters
 * - Handling removed waiters
 * - Managing authentication state
 * - Socket event handling for waiter authentication changes
 */
@Injectable({
  providedIn: 'root',
})
export class WaiterAuthService {
  private readonly globalConfirmService = inject(GlobalConfirmService);
  private readonly translateService = inject(TranslateService);
  private readonly socketConnectionHandlerService = inject(
    SocketConnectionHandlerService
  );
  private readonly accountSocketHandlerService = inject(
    AccountSocketHandlerService
  );
  private readonly accountModeService = inject(AccountModeService);
  private readonly logger = inject(Logger);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly deviceService = inject(DeviceService);
  private readonly eventBus = inject(EVENT_BUS);
  private onWaiterSignOut: Subject<void> = new Subject<void>();

  private readonly serviceStateService = inject(SERVICE_STATE_SERVICE);

  /**
   * Observable that emits when a waiter signs out
   */
  readonly onWaiterSignOut$ = this.onWaiterSignOut.asObservable();

  /**
   * Initializes the service and sets up socket listeners
   */
  constructor() {
    this.initCurrentWaiterRemovedListener();
    this.initCurrentWaiterNotFoundListener();
  }

  /**
   * Notifies that the current waiter has signed out
   * @returns {void}
   */
  notifyWaiterSignOut(): void {
    this.onWaiterSignOut.next();
  }

  /**
   * Signs out the current waiter and updates account mode if necessary
   * @returns {Observable<void>} Observable of the sign out process
   */
  signOutWaiter(): Observable<void> {
    return of(void 0).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(() => {
        const mode = this.accountModeService.accountMode;

        if (mode !== ACCOUNT_MODE.default && mode !== ACCOUNT_MODE.initial) {
          return this.accountModeService
            .insertAccountMode(ACCOUNT_MODE.default)
            .pipe(
              takeUntilDestroyed(this.destroyRef),
              map(() => void 0)
            );
        }

        return of(void 0);
      }),
      switchMap(() => this.deviceService.getDevice()),
      takeUntilDestroyed(this.destroyRef),
      switchMap((device) => {
        if (device?.personal_use) {
          this.serviceStateService.removeCurrentDrawer();
        }

        return of(void 0);
      }),
      tap(() => {
        this.accountSocketHandlerService.sendOpenAccountsSyncEvent();
        this.notifyWaiterSignOut();
      }),
      tap(() => this.eventBus.publish(new WaiterSessionEndedEvent())),
      catchError((error) => {
        this.logger.error(
          new Error('[WaiterAuthService][signOutWaiter] error:'),
          error
        );

        return of(void 0);
      })
    );
  }

  /**
   * Validates waiter password
   * @param {IAuthWaiter} waiterAuth - Authentication credentials
   * @param {IWaiter} selectedWaiter - Selected waiter to validate
   * @returns {boolean} True if password is invalid, false if valid
   */
  private isInvalidPassword(waiterAuth: IAuthWaiter, selectedWorker: Worker): boolean {
    if (!waiterAuth.password) {
      return false; // No password provided, consider valid (optional password)
    }

    if (waiterAuth.password !== selectedWorker.password) {
      this.logger.error(
        new Error('[WaiterAuthService][isInvalidPassword] Invalid password provided')
      );

      return true; // Invalid password
    }

    return false; // Valid password
  }

  /**
   * Validates waiter data integrity
   * @param {IAuthWaiter} waiterAuth - Authentication credentials
   * @param {IWaiter} selectedWaiter - Selected waiter to validate
   * @returns {boolean} True if waiter data is invalid, false if valid
   */
  private isInvalidWaiterData(waiterAuth: IAuthWaiter, selectedWorker: Worker): boolean {
    if (!waiterAuth || !selectedWorker || !waiterAuth.waiter_id) {
      this.logger.error(
        new Error('[WaiterAuthService][isInvalidWaiterData] waiterAuth, selectedWorker or waiter_id is null or undefined')
      );

      return true;
    }

    return false;
  }

  /**
   * Signs in a waiter if authentication credentials are valid
   *
   * @param {IAuthWaiter} waiterAuth - Authentication credentials
   * @param {IWaiter} selectedWaiter - Waiter data selected
   * @returns {Observable<boolean>} Observable with true if sign in succeeded
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  signInWaiter(waiterAuth: IAuthWaiter, selectedWorker: Worker
  ): Observable<boolean> {

    if (this.isInvalidWaiterData(waiterAuth, selectedWorker)) {
      return of(false);
    }

    if (this.isInvalidPassword(waiterAuth, selectedWorker)) {
      return of(false);
    }

    this.serviceStateService.setCurrentWorker(selectedWorker);

    return of(selectedWorker).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((currentWorker: Worker | null) => !!currentWorker),
      catchError((error) => {
        this.logger.error(
          new Error('[WaiterAuthService][signInWaiter] error:'),
          error
        );

        return of(false);
      })
    );
  }

  /**
   * Subscribes to the event when the current waiter is removed
   * It handles sign out, socket events and opens a confirm dialog
   * @private
   */
  private initCurrentWaiterRemovedListener(): void {
    // TODO: Implement this carlos.florio check if we need to notify on current waiter removed
    /*this.waiterService.onCurrentWaiterRemoved$
      .pipe(
        mergeMap(() => this.signOutWaiter()),
        mergeMap(() => this.waiterService.getWaitersFromLocal()),
        catchError((error) => {
          this.logger.error(
            new Error(
              '[WaiterAuthService][initCurrentWaiterRemovedListener] error:'
            ),
            error
          );

          return of([]);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((waiters: IWaiter[]) => {
        this.logger.debug(
          `[Socket 🔌] [Room: ${SOCKET_ROOM.waiters} 🙎‍♂️🙍‍♀️] [Current waiter has been removed]`
        );
        this.socketConnectionHandlerService.sendUnsentSocketEvents();
        const route = this.determineRoute(waiters);

        this.openConfirmDialog({
          titleKey: 'fv_confirm.current_waiter_removed.title',
          messageKey: 'fv_confirm.current_waiter_removed.subtitle',
          route,
        });
      });*/
  }

  /**
   * Subscribes to the event when the current waiter is not found
   * It handles sign out, socket events and opens a confirm dialog
   * @private
   */
  private initCurrentWaiterNotFoundListener(): void {
    // TODO: Implement this carlos.florio check if we need to notify on current waiter not found
    /*this.waiterService.onCurrentWaiterNotFound$
      .pipe(
        mergeMap(() => this.signOutWaiter()),
        mergeMap(() => this.waiterService.getWaitersFromLocal()),
        catchError((error) => {
          this.logger.error(
            new Error(
              '[WaiterAuthService][initCurrentWaiterNotFoundListener]  error:'
            ),
            error
          );

          return of([]);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((waiters: IWaiter[]) => {
        this.logger.debug(
          `[Socket 🔌] [Room: ${SOCKET_ROOM.waiters} 🙎‍♂️🙍‍♀️] [Current waiter not found]`
        );
        this.socketConnectionHandlerService.sendUnsentSocketEvents();
        const route = this.determineRoute(waiters);

        this.openConfirmDialog({
          titleKey: 'fv_snackbar.current_waiter_not_found',
          route,
        });
      });*/
  }

  /**
   * Opens a confirmation dialog with the provided options
   *
   * @param {Object} options - Object containing keys for translation and navigation route
   * @param {string} options.titleKey - Translation key for the dialog title
   * @param {string} [options.messageKey] - Translation key for the dialog message
   * @param {string[]} options.route - Route to navigate to when confirmed
   * @private
   */
  private openConfirmDialog(options: {
    titleKey: string;
    messageKey?: string;
    route: string[];
  }): void {
    const dialogConfig = {
      noClose: true,
      icon: 'fas fa-face-frown',
      iconSize: 'text-3xl',
      title: this.translateService.instant(options.titleKey),
      hideCloseIcon: true,
      buttons: [
        {
          text: this.translateService.instant('global.understood'),
          color: BUTTON_COLOR.primary,
          size: BUTTON_SIZE.full,
          type: BUTTON_TYPE.filled,
          preventAutoClose: true,
          handler: (): void => {
            this.globalConfirmService.close();
            this.router.navigate(options.route, {replaceUrl: true});
          },
        },
      ],
    };

    if (options.messageKey) {
      Object.assign(dialogConfig, {
        message: this.translateService.instant(options.messageKey),
      });
    }

    this.globalConfirmService.open(dialogConfig);
  }

  /**
   * Determines the navigation route based on the list of waiters
   *
   * @param {IWaiter[]} waiters - Array of waiters
   * @returns {string[]} Route to navigate
   * @private
   */
  private determineRoute(waiters: IWaiter[]): string[] {
    return waiters && waiters.length
      ? [APP_ROUTE_PATH.serviceConfiguration]
      : [APP_ROUTE_PATH.info, INFORMATIVE_ROUTE_PATH.workers];
  }
}
